import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import { ColumnMetaData, RealTimeStockRowsData } from "../../../types/types";
import ExpandableTableRow from "./ExpandableTableRow";
import ShelfLifeCustomFooter from "../../../components/dms_common/ShelfLifeCustomFooter";
import "./RealTimeStockTable.css";
import { logAnalyticsEvent } from "../../../firebase";
import store from "../../../stateManagement/store";
import { useSelector } from "react-redux";

interface RealTimeStockTableProps {
  rows: RealTimeStockRowsData[];
  columnsMetaData: ColumnMetaData<RealTimeStockRowsData>[];
  filterParam?: string | null;
  stockTypeParam?: string | null;
  defaultSelectedOption?: string;
  tableWidth?: number;
  isCheckboxSelection?: boolean;
  horizontalScrollBehaviour?:boolean
}

const RealTimeStockTable: React.FC<RealTimeStockTableProps> = ({ rows, columnsMetaData, filterParam, stockTypeParam, defaultSelectedOption,tableWidth,isCheckboxSelection,horizontalScrollBehaviour=false, }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption || "availableQty");
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedRowIds([]);
  };

  useEffect(() => {
    setPage(0);
  }, [rows]);

  useEffect(() => {
    if (defaultSelectedOption && defaultSelectedOption !== selectedOption) {
      setSelectedOption(defaultSelectedOption);
    }
  }, [defaultSelectedOption]);

  const handleChangeRowsPerPage: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    setSelectedOption(event.target.value);
    logAnalyticsEvent( "real_time_stock_filter", {
      "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
      "stock_type_selected":event.target.value
    });
  };

  const sidebarCollapsed: boolean = useSelector(
    () => store.getState().sidebarState.sidebarCollapsed
  );
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarCollapsed]);

  const [widthFixedColumns, setWidthFixedColumns] = useState(columnsMetaData);

  useEffect(() => {
    let maximumTableWidth =
      (tableWidth ?? 0.75) * screenWidth - (isCheckboxSelection ? 50 : 0);

    if (sidebarCollapsed) maximumTableWidth += screenWidth * 0.079861;

    const currentTotalColumnsWidth = columnsMetaData?.reduce(
      (acc, column) => acc + column.width,
      0
    );
    const difference = Math.round(maximumTableWidth - currentTotalColumnsWidth);

    const newColumnsData = columnsMetaData.map((column) => {
      if (horizontalScrollBehaviour) return column;

      const ratio = column.width / currentTotalColumnsWidth;

      if (difference > 0) {
        const additionalWidth = Math.round(ratio * difference);
        return {
          ...column,
          width: column.width + additionalWidth,
        };
      } else if (difference < 0) {
        const reductionWidth = Math.round(ratio * -difference * 1.5);
        return {
          ...column,
          width: Math.max(column.width - reductionWidth, 0),
        };
      }
      return column;
    });

    setWidthFixedColumns(newColumnsData);
  }, [screenWidth, columnsMetaData, sidebarCollapsed]);

  const sortedRows = rows.sort((a, b) => {
    if (filterParam === "outOfStock" && stockTypeParam === "availableQty") {
      const aHasZeroStock = (a.pieceAvailableQty === 0 && a.caseAvailableQty === 0 && a.otherAvailableQty === 0);
      const bHasZeroStock = (b.pieceAvailableQty === 0 && b.caseAvailableQty === 0 && b.otherAvailableQty === 0);

      if (aHasZeroStock && !bHasZeroStock) return -1;
      if (!aHasZeroStock && bHasZeroStock) return 1;

      if (aHasZeroStock === bHasZeroStock) {
        const timeA = a.lastModifiedTime ? new Date(a.lastModifiedTime).getTime() : 0;
        const timeB = b.lastModifiedTime ? new Date(b.lastModifiedTime).getTime() : 0;
        return timeB - timeA;
      }
    }
    
    const timeA = a.lastModifiedTime ? new Date(a.lastModifiedTime).getTime() : 0;
    const timeB = b.lastModifiedTime ? new Date(b.lastModifiedTime).getTime() : 0;
    return timeB - timeA; // Descending order (most recent first)
  });

  const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangeExpandedRows = (rowId: string) => {
    // // 
    logAnalyticsEvent( "real_time_batch_wise_view", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "total_batches_displayed":0
          });
    if (expandedRowIds.includes(rowId)) {
      setExpandedRowIds(expandedRowIds.filter((id) => id !== rowId));
    } else {
      setExpandedRowIds([...expandedRowIds, rowId]);
    }
  };
  const getSelectedStyle = (value: string) => {
    switch (value) {
      case "committedQty":
        return { color: "#1f8792", backgroundColor: "#d7f2f5" }; // blue
      case "availableQty":
        return { color: "#2e7d32", backgroundColor: "#dcf2e0" }; // green
      case "unsellableQty":
        return { color: "#cd6970", backgroundColor: "#FFEEEF" }; // orange
      case "onRouteQty":
        return { color: "#838624", backgroundColor: "#f2f3d7" }; // purple
      case "inTransitQty":
        return { color: "#B05A2B", backgroundColor: "#FFF7F0" };
      default:
        return { color: "#227c9d", backgroundColor: "#f0fbff" }; // default
    }
  };
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  return (
    <div className="RealTimeStockTable_container">
      <TableContainer
        sx={{
          overflowX: "hidden",
          overflowY: "auto",
          maxHeight: "90%",
          scrollbarWidth: ConfigDictionary?.showTableScrollbar ? "thin" : "none",
          ...(!(ConfigDictionary?.showTableScrollbar) && {
            "&::-webkit-scrollbar": {
              display: "none",
            },
          })
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: widthFixedColumns.reduce((sum, column) => sum + (column.width || 0), 0),
            "& .MuiTable-root .MuiTableHead-root .MuiTableRow-head": {
              height: "50px",
            },
            "& .MuiTableCell-stickyHeader": {
              height: "50px",
            },
            "& .MuiTableCell-root": {
              borderColor: "#eaeaea",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {widthFixedColumns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    textAlign: `${column.headerAlign}`,
                    width: `${column.width}px`,
                    color: "#227c9d",
                    backgroundColor: "#f0fbff",
                    fontWeight: "600",
                    fontSize: "14px",
                    padding: "5px 0",
                    height: "50px",
                    "& .MuiTableCell-stickyHeader": {
                      height: "50px",
                    },
                    paddingLeft: column?.paddingLeft ?? 0,
                    paddingRight: column?.paddingRight ?? 0,
                  }}
                >
                  {column.headerName}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  width: `200px`,
                  color: "#227c9d",
                  backgroundColor: "#f0fbff",
                  fontWeight: "600",
                  fontSize: "14px",
                  paddingLeft: "6px",
                }}
              >
                <Select
                  value={selectedOption}
                  onChange={handleOptionChange}
                  sx={{
                    border: "none",
                    fontSize: "14px",
                    fontWeight: "600",
                    borderRadius: "10px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      padding: "5px 12px",
                    },
                    "& .MuiSelect-icon": {
                      color: getSelectedStyle(selectedOption).color,
                      transition: "color 0.3s ease",
                    },
                    transition: "color 0.3s ease, background-color 0.3s ease",
                    ...getSelectedStyle(selectedOption),
                  }}
                >
                  <MenuItem value="availableQty" sx={{ border: "none", color: "#909090" }}>
                    Available Stock
                  </MenuItem>
                  <MenuItem value="committedQty" sx={{ border: "none", color: "#909090" }}>
                    Committed Stock
                  </MenuItem>
                  <MenuItem value="onRouteQty" sx={{ border: "none", color: "#909090" }}>
                    On Route Stock
                  </MenuItem>
                  <MenuItem value="unsellableQty" sx={{ border: "none", color: "#909090" }}>
                    Unsellable Stock
                  </MenuItem>
                  <MenuItem value="inTransitQty" sx={{ border: "none", color: "#909090" }}>
                    In-Transit Stock
                  </MenuItem>
                </Select>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={widthFixedColumns.length + 1}
                  align="center"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: "500",
                    fontSize: "13px",
                    color: "#909090",
                    padding: "20px",
                    whiteSpace: "nowrap",
                    border: "none",
                  }}
                >
                  No Search results found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <ExpandableTableRow
                  key={rowIndex}
                  row={row}
                  columnsMetaData={widthFixedColumns}
                  selectedOption={selectedOption}
                  expandedRowIds={expandedRowIds}
                  handleChangeExpandedRows={handleChangeExpandedRows}
                  filterParam={filterParam}
                  stockTypeParam={stockTypeParam}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          bottom: 0,
          borderTop: "1px solid #eaeaea",
          overflowY: "hidden",
          width: "100%",
          maxHeight: "46px"
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& p": {
              margin: 0,
            },
          }}
        />
        {expandedRowIds.length > 0 && <ShelfLifeCustomFooter />}
      </Box>
    </div>
  );
};

export default RealTimeStockTable;
